import { useEffect, useState, type RefObject } from 'react'
import type { VirtualElement } from '@floating-ui/react-dom'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { createTextareaCaretAnchor } from 'tripPlanner/components/Chat/utils/textareaCaret'

// Following every keystroke would drag the nudge along under the traveller's eyes; it moves once they pause.
const CARET_SETTLE_MS = 400

interface Options {
  /** What sits around the text, such as chips above it; the anchor starts at the top of its content */
  frame: Element | null
  /** The text arrived whole, such as a tapped answer, so the anchor moves without waiting for a pause */
  isWhole: boolean
}

function getCaretIndex(textarea: HTMLTextAreaElement): number {
  return textarea.selectionDirection === 'backward' ? textarea.selectionStart : textarea.selectionEnd
}

/** An anchor above the textarea's caret, moved once the caret has stayed put for a moment. */
export function useCaretAnchor(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  text: string,
  { frame, isWhole }: Options,
): VirtualElement | null {
  const [anchor, setAnchor] = useState<VirtualElement | null>(null)

  const settle = useDebouncedCallback(() => {
    const textarea = textareaRef.current
    if (textarea) setAnchor(createTextareaCaretAnchor(textarea, getCaretIndex(textarea), frame ?? textarea))
  }, CARET_SETTLE_MS, [frame, textareaRef])

  // Before paint, so a question that follows a tapped answer never shows at the old caret first.
  useIsomorphicLayoutEffect(() => {
    settle()
    if (isWhole) {
      settle.flush()
    }
  }, [isWhole, settle, text])

  useEffect(() => {
    const handleSelectionChange = () => {
      if (document.activeElement === textareaRef.current) settle()
    }
    document.addEventListener('selectionchange', handleSelectionChange)
    return () => document.removeEventListener('selectionchange', handleSelectionChange)
  }, [settle, textareaRef])

  return anchor
}
